import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../../utils/echarts'
import type { TFunction } from 'i18next'
import type { TaxYearlyData } from '@popety-io/types'
import { Icon } from '../../../atoms/Icon'
import { formatCHF } from '../taxService'

interface TaxAmountEvolutionChartProps {
  data: TaxYearlyData[]
  height?: number
  t: TFunction
}

export interface TaxAmountEvolutionChartRef {
  exportChart: () => string | null
}

export const TaxAmountEvolutionChart = forwardRef<
  TaxAmountEvolutionChartRef,
  TaxAmountEvolutionChartProps
>(({ data, height = 350, t }, ref) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useImperativeHandle(ref, () => ({
    exportChart: () => {
      if (chartInstance.current) {
        return chartInstance.current.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        })
      }
      return null
    },
  }))

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return

    // Initialize chart
    chartInstance.current = safeChartInit(chartRef.current)

    // Sort data by year
    const sortedData = [...data].sort((a, b) => a.year - b.year)

    // Prepare data arrays
    const years = sortedData.map((d) => d.year.toString())
    const singleData = sortedData.map((d) => d.total_tax_single)
    const marriedData = sortedData.map((d) => d.total_tax_married)
    const familyData = sortedData.map((d) => d.total_tax_family)

    // Build series for all profiles
    const series: echarts.SeriesOption[] = [
      {
        name: t('profiles.single'),
        type: 'line',
        data: singleData,
        lineStyle: { color: '#3b82f6', width: 2 },
        itemStyle: { color: '#3b82f6' },
        symbol: 'circle',
        symbolSize: 5,
        smooth: true,
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(59, 130, 246, 0.2)' },
            { offset: 1, color: 'rgba(59, 130, 246, 0.02)' },
          ]),
        },
      },
      {
        name: t('profiles.married'),
        type: 'line',
        data: marriedData,
        lineStyle: { color: '#10b981', width: 2 },
        itemStyle: { color: '#10b981' },
        symbol: 'diamond',
        symbolSize: 5,
        smooth: true,
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(16, 185, 129, 0.2)' },
            { offset: 1, color: 'rgba(16, 185, 129, 0.02)' },
          ]),
        },
      },
      {
        name: t('profiles.family'),
        type: 'line',
        data: familyData,
        lineStyle: { color: '#f59e0b', width: 2 },
        itemStyle: { color: '#f59e0b' },
        symbol: 'triangle',
        symbolSize: 5,
        smooth: true,
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(245, 158, 11, 0.2)' },
            { offset: 1, color: 'rgba(245, 158, 11, 0.02)' },
          ]),
        },
      },
    ]

    const legendData = series.map((s) => s.name as string)

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const year = params[0].axisValue
          let content = `<strong>${year}</strong><br/>`
          params.forEach((p: any) => {
            content += `<span style="color:${p.color}">●</span> ${p.seriesName}: CHF ${p.value.toLocaleString('de-CH')}<br/>`
          })
          return content
        },
      },
      legend: {
        data: legendData,
        top: 10,
        textStyle: { fontSize: 12 },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: years,
        axisLabel: { fontSize: 11, color: '#1f2937' },
        axisLine: { lineStyle: { color: '#e5e7eb' } },
      },
      yAxis: {
        type: 'value',
        name: t('charts.taxAmountEvolution.axis.amount'),
        axisLabel: {
          fontSize: 11,
          color: '#1f2937',
          formatter: (value: number) => {
            if (value >= 1000) {
              return `${(value / 1000).toFixed(0)}k`
            }
            return value.toString()
          },
        },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: '#f3f4f6', type: 'dashed' } },
      },
      series,
    }

    chartInstance.current.setOption(option)

    // Handle resize
    const handleResize = () => chartInstance.current?.resize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chartInstance.current?.dispose()
    }
  }, [data, height, t])

  if (!data || data.length === 0) {
    return (
      <div
        style={{
          height: `${height}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6b7280',
        }}
      >
        {t('charts.noData')}
      </div>
    )
  }

  // Calculate insights
  const sortedData = [...data].sort((a, b) => a.year - b.year)
  const firstYear = sortedData[0]
  const lastYear = sortedData[sortedData.length - 1]

  const avgSingleChange = lastYear.total_tax_single - firstYear.total_tax_single
  const avgMarriedChange =
    lastYear.total_tax_married - firstYear.total_tax_married
  const avgFamilyChange = lastYear.total_tax_family - firstYear.total_tax_family

  return (
    <div>
      <div
        ref={chartRef}
        style={{ width: '100%', height: `${height}px`, minHeight: '300px' }}
      />

      {/* Trend insights */}
      <div
        style={{
          marginTop: '16px',
          padding: '12px 16px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
        }}
      >
        <div
          style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Icon name="wallet" size="xs" />{' '}
          {t('charts.taxAmountEvolution.summary.title', {
            startYear: firstYear.year,
            endYear: lastYear.year,
          })}
        </div>
        <div style={{ fontSize: '13px', color: '#4b5563', lineHeight: '1.4' }}>
          • <strong>{t('profiles.single')}:</strong>{' '}
          {avgSingleChange >= 0 ? '+' : ''}
          {formatCHF(avgSingleChange)}
          <br />• <strong>{t('profiles.married')}:</strong>{' '}
          {avgMarriedChange >= 0 ? '+' : ''}
          {formatCHF(avgMarriedChange)}
          <br />• <strong>{t('profiles.family')}:</strong>{' '}
          {avgFamilyChange >= 0 ? '+' : ''}
          {formatCHF(avgFamilyChange)}
        </div>
      </div>
    </div>
  )
})

TaxAmountEvolutionChart.displayName = 'TaxAmountEvolutionChart'
